import React from 'react';
import { Tool } from '@/types/tool';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, Github, Play } from 'lucide-react';
import ScreenshotCarousel from './ScreenshotCarousel';
import { useLanguage } from '@/context/LanguageContext';
import { TranslationKey } from '@/translations/translations';

// Import tools data
import { getToolById } from '@/data/tools';

type ToolEmbedSection = 'overview' | 'features' | 'installation' | 'usage' | 'examples' | 'contribution' | 'screenshots' | 'all';

interface ToolEmbedProps {
  toolId: string;
  sections: ToolEmbedSection[];
  showHeader?: boolean;
  showActions?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

const ToolEmbed: React.FC<ToolEmbedProps> = ({
  toolId,
  sections,
  showHeader = true,
  showActions = true,
  className = '',
  style = {}
}) => {
  const { t, language } = useLanguage();
  const tool = getToolById(toolId);

  if (!tool) {
    return (
      <Card className={`p-6 border-destructive/20 bg-destructive/5 ${className}`}>
        <p className="text-destructive">Tool "{toolId}" not found.</p>
      </Card>
    );
  }

  const renderSection = (section: ToolEmbedSection) => {
    const content = tool.content[section as keyof typeof tool.content] as unknown;

    if (!content) return null;

    switch (section) {
      case 'overview':
        return (
          <div className="prose prose-sm max-w-none">
            <p className="text-indigo-200">{t(content as unknown as TranslationKey)}</p>
          </div>
        );

      case 'features':
        return (
          <ul className="space-y-2">
            {(content as string[]).map((feature, index) => (
              <li key={index} className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span className="text-sm">{t(feature as unknown as TranslationKey)}</span>
              </li>
            ))}
          </ul>
        );

      case 'installation':
        return (
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-semibold mb-2">Installation</h4>
            <pre
              className="text-sm bg-background p-3 rounded border overflow-x-auto"
              tabIndex={0}
              aria-label="Code snippet"
            >
              <code>{t(content as unknown as TranslationKey)}</code>
            </pre>
          </div>
        );

      case 'usage':
        return (
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-semibold mb-2">Usage</h4>
            <pre
              className="text-sm bg-background p-3 rounded border overflow-x-auto"
              tabIndex={0}
              aria-label="Code snippet"
            >
              <code>{t(content as unknown as TranslationKey)}</code>
            </pre>
          </div>
        );

      case 'examples':
        return (
          <div className="space-y-3">
            <h4 className="font-semibold">Examples</h4>
            {(content as string[]).map((example, index) => (
              <div key={index} className="bg-muted/30 rounded-lg p-3">
                <pre
                  className="text-sm overflow-x-auto"
                  tabIndex={0}
                  aria-label="Code snippet"
                >
                  <code>{t(example as unknown as TranslationKey)}</code>
                </pre>
              </div>
            ))}
          </div>
        );

      case 'screenshots': {
        // We need to translate the screenshots content
        const translatedScreenshots = tool.content.screenshots?.map(s => ({
          ...s,
          title: t(s.title as unknown as TranslationKey),
          description: t(s.description as unknown as TranslationKey),
          caption: s.caption ? t(s.caption as unknown as TranslationKey) : undefined
        }));

        return (
          <div>
            {translatedScreenshots && translatedScreenshots.length > 0 ? (
              <ScreenshotCarousel screenshots={translatedScreenshots} />
            ) : (
              <p className="text-muted-foreground text-sm">No screenshots available.</p>
            )}
          </div>
        );
      }

      case 'contribution':
        return (
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
            <h4 className="font-semibold mb-2">Contributing</h4>
            <p className="text-sm text-muted-foreground">{t(content as unknown as TranslationKey)}</p>
          </div>
        );

      case 'all':
        return (
          <div className="space-y-6">
            {tool.content.overview && renderSection('overview')}
            {tool.content.features && renderSection('features')}
            {tool.content.installation && renderSection('installation')}
            {tool.content.usage && renderSection('usage')}
            {tool.content.examples && renderSection('examples')}
            {tool.content.screenshots && renderSection('screenshots')}
            {tool.content.contribution && renderSection('contribution')}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className={`p-6 ${className}`} style={style}>
      {showHeader && (
        <div className="mb-6">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-xl font-semibold">{t(tool.name as unknown as TranslationKey)}</h3>
              <p className="text-muted-foreground">{t(tool.description as unknown as TranslationKey)}</p>
            </div>
            <Badge variant={tool.status === 'active' ? 'default' : 'secondary'}>
              {tool.status}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {tool.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Always render overview first if it exists */}
        {sections.includes('overview') && (
          <div>
            {renderSection('overview')}
          </div>
        )}

        {/* Check if we need special layout for features and screenshots */}
        {sections.includes('features') && sections.includes('screenshots') ? (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                {renderSection('features')}
              </div>
              <div>
                {renderSection('screenshots')}
              </div>
            </div>
            {/* Render remaining sections */}
            {sections.filter(section => section !== 'overview' && section !== 'features' && section !== 'screenshots').map((section) => (
              <div key={section}>
                {renderSection(section)}
              </div>
            ))}
          </>
        ) : (
          // Normal layout - render remaining sections in order (excluding overview since it's already rendered)
          sections.filter(section => section !== 'overview').map((section) => (
            <div key={section}>
              {renderSection(section)}
            </div>
          ))
        )}
      </div>

      {showActions && (
        <div className="flex gap-3 mt-6 pt-6 border-t">
          {tool.githubUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={tool.githubUrl} target="_blank" rel="noopener noreferrer">
                <Github className="mr-2 h-4 w-4" />
                GitHub
                <span className="sr-only">
                  {language === 'de' ? ' (öffnet in neuem Tab)' : ' (opens in a new tab)'}
                </span>
              </a>
            </Button>
          )}
          {tool.demoUrl && !tool.demoUrls && (
            <Button variant="outline" size="sm" asChild>
              <a href={tool.demoUrl} target="_blank" rel="noopener noreferrer">
                <Play className="mr-2 h-4 w-4" />
                Demo
                <span className="sr-only">
                  {language === 'de' ? ' (öffnet in neuem Tab)' : ' (opens in a new tab)'}
                </span>
              </a>
            </Button>
          )}
          {tool.demoUrls && tool.demoUrls.map((demo, index) => (
            <Button key={index} variant="outline" size="sm" asChild>
              <a href={demo.url} target="_blank" rel="noopener noreferrer">
                <Play className="mr-2 h-4 w-4" />
                {demo.label}
                <span className="sr-only">
                  {language === 'de' ? ' (öffnet in neuem Tab)' : ' (opens in a new tab)'}
                </span>
              </a>
            </Button>
          ))}
          {tool.documentation && (
            <Button variant="outline" size="sm" asChild>
              <a href={tool.documentation} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />
                Docs
                <span className="sr-only">
                  {language === 'de' ? ' (öffnet in neuem Tab)' : ' (opens in a new tab)'}
                </span>
              </a>
            </Button>
          )}
        </div>
      )}
    </Card>
  );
};

export default ToolEmbed;
